// SmartAppBanner.jsx
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from "react-i18next";
import {Image} from "@mui/icons-material";

const IS_IOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
const IS_ANDROID = /Android/i.test(navigator.userAgent);

const qs = (obj) =>
    Object.entries(obj || {})
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
        .join('&');

const nowSec = () => Math.floor(Date.now() / 1000);

function SmartAppBanner({
                            appName,
                            iosAppStoreId,            // "1234567890"
                            iosStoreUrl,              // опционально: прямая ссылка в App Store
                            androidPackage,           // "com.example.app"
                            androidStoreUrl,          // опционально: прямая ссылка в Google Play
                            deepLink,                 // (platform) => "myapp://" или "https://app.domain/...”
                            campaignParams = {},      // { inviterId: "123", campaign: "summer" }
                            autoTryOpen = false,
                            dismissTtlHours = 24,
                        }) {
    const {t, i18n} = useTranslation();
    const [visible, setVisible] = useState(false);
    const timerRef = useRef(null);
    const triedAutoRef = useRef(false);
    const hiddenAtRef = useRef(null);

    const store = useMemo(() => {
        const params = {...campaignParams};
        const ios = iosStoreUrl
            ? `${iosStoreUrl}${iosStoreUrl.includes('?') ? '&' : '?'}${qs(params)}`
            : `https://apps.apple.com/app/id${iosAppStoreId}`;
        const play = androidStoreUrl
            ? `${androidStoreUrl}${androidStoreUrl.includes('?') ? '&' : '?'}${qs(params)}`
            : `https://play.google.com/store/apps/details?id=${androidPackage}&${qs(params)}`;
        return {ios, play};
    }, [iosStoreUrl, iosAppStoreId, androidStoreUrl, androidPackage, campaignParams]);

    useEffect(() => {
        setVisible(true);
    }, []);

    // нативный Safari Smart App Banner (дополнительно к нашему)
    useEffect(() => {
        if (!IS_IOS) return;
        const meta = document.createElement('meta');
        meta.name = 'apple-itunes-app';
        meta.content = `app-id=${iosAppStoreId}`;
        document.head.appendChild(meta);
        return () => {
            document.head.removeChild(meta);
        };
    }, [iosAppStoreId]);

    // авто-открытие + фолбэк в стор
    useEffect(() => {
        if (!visible || !autoTryOpen || triedAutoRef.current) return;
        triedAutoRef.current = true;

        const onVisibility = () => {
            if (document.hidden) hiddenAtRef.current = Date.now();
        };
        document.addEventListener('visibilitychange', onVisibility);

        const openApp = () => {
            console.log('SmartAppBanner: try open app');
            const dl = typeof deepLink === 'function' ? deepLink(IS_IOS ? 'ios' : 'android') : null;
            if (!dl) return;

            if (IS_ANDROID) {
                const isChrome = /Chrome/i.test(navigator.userAgent) && !/Edge/i.test(navigator.userAgent);

                if (isChrome && !dl.startsWith('http')) {
                    // Intent URL для Chrome
                    const scheme = dl.split('://')[0];
                    const path = dl.replace(/^[a-z][a-z0-9+.-]*:\/\//i, '');
                    const intentUrl = `intent://${path}#Intent;scheme=${scheme};package=${androidPackage};end`;
                    window.location.href = intentUrl;
                } else {
                    window.location.href = dl;
                }
            } else if (IS_IOS) {
                if (dl.startsWith('http')) {
                    window.location.href = dl;
                } else {
                    const iframe = document.createElement('iframe');
                    iframe.style.display = 'none';
                    iframe.src = dl;
                    document.body.appendChild(iframe);
                    setTimeout(() => document.body.removeChild(iframe), 1500);
                }
            }
        };

        openApp();

        timerRef.current = window.setTimeout(() => {
            const notSwitched = !document.hidden && !hiddenAtRef.current;
            if (notSwitched) {
                window.location.href = IS_IOS ? store.ios : store.play;
            }
            document.removeEventListener('visibilitychange', onVisibility);
        }, IS_IOS ? 1500 : 1200);

        return () => {
            if (timerRef.current) window.clearTimeout(timerRef.current);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [visible, autoTryOpen, deepLink, store]);

    if (!visible) return null;

    function tryOpenDeepLink({
                                 platform,        // 'ios' | 'android'
                                 deepLink,        // строка deeplink (scheme или https)
                                 androidPackage,  // 'com.terminka.client' или 'com.terminka.business'
                                 fallbackStore,   // URL App Store / Google Play
                                 timeoutMs = 1500 // задержка до фолбэка
                             }: {
        platform: 'ios' | 'android',
        deepLink: string,
        androidPackage?: string,
        fallbackStore: string,
        timeoutMs?: number
    }) {
        return new Promise((resolve) => {
            let didSwitch = false;
            const start = Date.now();

            const onVisibility = () => {
                // Если страница ушла в бэкграунд — почти наверняка пользователь попал в приложение
                if (document.hidden) {
                    didSwitch = true;
                }
            };

            const onPageHide = () => {
                didSwitch = true;
            };

            document.addEventListener('visibilitychange', onVisibility);
            window.addEventListener('pagehide', onPageHide);
            window.addEventListener('blur', onVisibility);

            const cleanup = () => {
                document.removeEventListener('visibilitychange', onVisibility);
                window.removeEventListener('pagehide', onPageHide);
                window.removeEventListener('blur', onVisibility);
            };

            // Реальное открытие
            if (platform === 'android') {
                const isChrome = /Chrome/i.test(navigator.userAgent) && !/Edge|OPR/i.test(navigator.userAgent);
                if (isChrome && !deepLink.startsWith('http')) {
                    // intent:// с фолбэком — если не установлено, откроется Google Play
                    const scheme = deepLink.split('://')[0];
                    const path = deepLink.replace(/^[a-z][a-z0-9+.-]*:\/\//i, '');
                    const intentUrl = `intent://${path}#Intent;scheme=${scheme};package=${androidPackage};S.browser_fallback_url=${encodeURIComponent(fallbackStore)};end`;
                    window.location.href = intentUrl;
                } else {
                    window.location.href = deepLink;
                }
            } else { // iOS
                if (deepLink.startsWith('http')) {
                    // Universal Link — просто переходим, iOS сам решит открыть приложение
                    window.location.href = deepLink;
                } else {
                    // scheme: через скрытый iframe
                    const iframe = document.createElement('iframe');
                    iframe.style.display = 'none';
                    iframe.src = deepLink;
                    document.body.appendChild(iframe);
                    setTimeout(() => document.body.removeChild(iframe), 1200);
                }
            }

            // Таймер фолбэка
            setTimeout(() => {
                cleanup();
                // Если за время таймера страница не ушла в бэкграунд — считаем, что приложение не открылось
                if (!didSwitch && Date.now() - start >= timeoutMs) {
                    window.location.href = fallbackStore;
                    resolve(false);
                } else {
                    resolve(true);
                }
            }, timeoutMs);
        });
    }



    const onOpen = async () => {
        const dl = typeof deepLink === 'function' ? deepLink(IS_IOS ? 'ios' : 'android') : null;
        if (!dl) return;

        await tryOpenDeepLink({
            platform: IS_IOS ? 'ios' : 'android',
            deepLink: dl,
            androidPackage,
            fallbackStore: IS_IOS ? store.ios : store.play,
            timeoutMs: IS_IOS ? 1500 : 1200,
        });
    };

    return (
        <div
            style={{
                position: 'fixed',
                left: 12,
                right: 12,
                bottom: 12,
                zIndex: 9999,
                background: '#111',
                color: '#fff',
                borderRadius: 12,
                boxShadow: '0 10px 30px rgba(0,0,0,.35)',
                padding: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
            }}
        >
            <div style={{ width: 44, height: 44, borderRadius: 10, background: '#333' }}>
                <img
                    src="https://dqj9vh7783e16.cloudfront.net/terminka_logo.png"
                    width={44}
                    height={44}
                    alt="Terminka logo"
                />
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700 }}>{appName}</div>
                <div style={{ opacity: 0.8, fontSize: 12 }}>
                    {IS_IOS
                        ? `${t('availableIn')} App Store`
                        : IS_ANDROID
                            ? `${t('availableIn')} Google Play`
                            : `${t('availableIn')} Store`}
                </div>
            </div>

            {/* ОДНА КНОПКА */}
            <button
                onClick={onOpen}
                style={{
                    background: '#fff',
                    color: '#111',
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 12px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    minWidth: 110,
                }}
            >
                {t('open')}
            </button>

            <button
                onClick={() => setVisible(false)}
                style={{
                    background: 'transparent',
                    color: '#fff',
                    border: 'none',
                    padding: '6px 8px',
                    opacity: 0.7,
                    cursor: 'pointer',
                }}
                aria-label="Dismiss"
                title="Dismiss"
            >
                ✕
            </button>
        </div>
    );
}

export default SmartAppBanner;
